import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  useTheme,
  Tooltip
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Work,
  LocationOn,
  People,
  AttachMoney,
  ArrowBack,
  PersonSearch,
  ContentCopy,
  Check
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import {
  getJobOpenings,
  deleteJob,
  JobOpening
} from '@/services/DashboardAnalyticsService';

const statusColors: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  open: 'success',
  draft: 'default',
  closed: 'error',
  'on-hold': 'warning'
};

const JobPostingsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [jobs, setJobs] = useState<JobOpening[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<JobOpening | null>(null);
  const [copiedJobId, setCopiedJobId] = useState<number | null>(null);

  const handleCopyLink = async (jobId: number) => {
    const publicUrl = `${window.location.origin}/careers/${jobId}`;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopiedJobId(jobId);
      setTimeout(() => setCopiedJobId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const statusFilters = ['all', 'open', 'draft', 'closed', 'on-hold'];

  const fetchJobs = async (status?: string) => {
    setLoading(true);
    setError(null);
    try {
      const statusParam = status && status !== 'all' ? status : undefined;
      const response = await getJobOpenings(1, 50, statusParam);
      if (response.success && response.data) {
        setJobs(response.data);
      } else {
        setError(response.error?.message || 'Failed to load jobs');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const status = statusFilters[activeTab];
    fetchJobs(status);
  }, [activeTab]);

  const handleDeleteClick = (job: JobOpening) => {
    setJobToDelete(job);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!jobToDelete) return;

    try {
      const response = await deleteJob(jobToDelete.id);
      if (response.success) {
        setDeleteConfirmOpen(false);
        setJobToDelete(null);
        fetchJobs(statusFilters[activeTab]);
      } else {
        setError(response.error?.message || 'Failed to delete job');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete job');
    }
  };

  return (
    <Layout>
      <Container maxWidth="xl">
        <Box sx={{ py: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={() => navigate('/dashboard')}>
                <ArrowBack />
              </IconButton>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Job Postings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage job openings and recruitment
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<PersonSearch />}
                onClick={() => navigate('/hr/applications')}
              >
                View Applications
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/hr/jobs/new')}
              >
                Post New Job
              </Button>
            </Box>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Status Filter Tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ mb: 3 }}
          >
            {statusFilters.map((status) => (
              <Tab
                key={status}
                label={status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
              />
            ))}
          </Tabs>

          {/* Loading State */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : jobs.length === 0 ? (
            <Card sx={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
              textAlign: 'center',
              py: 8
            }}>
              <Work sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No job postings found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first job posting to start recruiting
              </Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/hr/jobs/new')}>
                Post New Job
              </Button>
            </Card>
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 3
            }}>
              {jobs.map((job) => (
                <Card key={job.id} sx={{
                  height: '100%',
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
                  border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: theme.shadows[4]
                  }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ flex: 1 }}>
                        {job.title}
                      </Typography>
                      <Chip
                        label={job.status.replace('-', ' ')}
                        color={statusColors[job.status] || 'default'}
                        size="small"
                      />
                    </Box>

                    {job.department && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {job.department}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {job.location && (
                        <Chip
                          icon={<LocationOn fontSize="small" />}
                          label={job.location}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {job.employment_type && (
                        <Chip
                          label={job.employment_type.replace('-', ' ')}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      <Chip
                        icon={<People fontSize="small" />}
                        label={`${job.positions_count} position${job.positions_count > 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    {(job.salary_min || job.salary_max) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                        <AttachMoney fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {job.salary_min && job.salary_max
                            ? `₹${job.salary_min.toLocaleString('en-IN')} - ₹${job.salary_max.toLocaleString('en-IN')}`
                            : job.salary_min
                              ? `From ₹${job.salary_min.toLocaleString('en-IN')}`
                              : `Up to ₹${job.salary_max?.toLocaleString('en-IN')}`}
                        </Typography>
                      </Box>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {job.description}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Posted by {job.posted_by_name}
                      </Typography>
                      <Box>
                        <Tooltip title={copiedJobId === job.id ? "Copied!" : "Copy public link"}>
                          <IconButton
                            size="small"
                            color={copiedJobId === job.id ? "success" : "default"}
                            onClick={() => handleCopyLink(job.id)}
                          >
                            {copiedJobId === job.id ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => navigate(`/hr/jobs/${job.id}/edit`)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(job)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
            <DialogTitle>Delete Job Posting</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete "{jobToDelete?.title}"? This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
              <Button color="error" variant="contained" onClick={handleDeleteConfirm}>
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    </Layout>
  );
};

export default JobPostingsPage;
